import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

const SectionContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#0f172a', // slate-900
  padding: '80px 24px',
  overflow: 'hidden',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    padding: '128px 48px'
  },
  [theme.breakpoints.up('lg')]: {
    padding: '128px 64px'
  }
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  margin: '0 auto'
}));

const HeaderWrapper = styled(Box)(({ theme }) => ({
  maxWidth: '768px',
  margin: '0 auto',
  textAlign: 'center',
  marginBottom: '64px',
  opacity: 0,
  transform: 'translateY(20px)',
  transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
  '&.is-visible': {
    opacity: 1,
    transform: 'translateY(0)'
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: '96px'
  }
}));

const Title = styled('h1')(({ theme }) => ({
  fontSize: '36px',
  fontWeight: 800,
  lineHeight: 1.2,
  letterSpacing: '-0.025em',
  color: '#ffffff',
  margin: 0,
  marginBottom: '24px',
  [theme.breakpoints.up('md')]: {
    fontSize: '48px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '60px'
  }
}));

const GradientText = styled('span')({
  background: 'linear-gradient(90deg, #22d3ee, #a5b4fc)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  display: 'inline'
});

const Subtitle = styled('p')(({ theme }) => ({
  marginTop: '24px',
  fontSize: '18px',
  color: '#94a3b8', // slate-400
  maxWidth: '672px',
  margin: '0 auto',
  lineHeight: 1.6,
  [theme.breakpoints.up('md')]: {
    fontSize: '20px'
  }
}));

const CardsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '32px',
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(3, 1fr)'
  }
}));

const Card = styled(Box)(({ theme, delay }) => ({
  position: 'relative',
  backgroundColor: '#1e293b', // slate-800
  border: '1px solid #334155', // slate-700
  borderRadius: '12px',
  padding: '32px',
  transition: 'border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease',
  opacity: 0,
  transform: 'translateY(20px)',
  transitionDelay: delay || '0ms',
  '&.is-visible': {
    opacity: 1,
    transform: 'translateY(0)'
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: '#22d3ee', // cyan-400
    boxShadow: '0 0 25px rgba(34, 211, 238, 0.2)'
  }
}));

const CardContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%'
});

const IconContainer = styled(Box)({
  marginBottom: '24px'
});

const IconBox = styled(Box)({
  backgroundColor: '#0f172a', // slate-900
  width: '56px',
  height: '56px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #334155' // slate-700
});

const CardTitle = styled('h3')({
  fontSize: '24px',
  fontWeight: 700,
  color: '#ffffff',
  marginBottom: '12px',
  lineHeight: 1.3
});

const CardDescription = styled('p')({
  color: '#94a3b8', // slate-400
  marginBottom: '24px',
  flexGrow: 1,
  fontSize: '16px',
  lineHeight: 1.6
});

const StatContainer = styled(Box)({
  marginTop: 'auto'
});

const StatNumber = styled('p')(({ theme }) => ({
  fontSize: '48px',
  fontWeight: 800,
  background: 'linear-gradient(90deg, #22d3ee, #a5b4fc)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  margin: 0,
  lineHeight: 1,
  [theme.breakpoints.up('lg')]: {
    fontSize: '60px'
  }
}));

const StatText = styled('p')({
  fontSize: '14px',
  color: '#64748b', // slate-500
  marginTop: '8px',
  lineHeight: 1.5
});

// SVG Icons
const SearchOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
    <line x1="4" y1="4" x2="20" y2="20"/>
  </svg>
);

const UsersIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);

const TrendingDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/>
    <polyline points="17 18 23 18 23 12"/>
  </svg>
);

const ProblemSection = () => {
  const headerRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const observerOptions = {
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    cardsRef.current.forEach(card => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const problems = [
    {
      icon: <SearchOffIcon />,
      title: 'Customers Can\'t Find You Online',
      description: 'Your business is invisible when customers search locally. If they can\'t find you, they can\'t choose you.',
      stat: '73%',
      statText: 'of customers can\'t find most local businesses online.',
      delay: '100ms'
    },
    {
      icon: <UsersIcon />,
      title: 'Competitors Dominate Search',
      description: 'Your competition appears first while you\'re nowhere to be found, capturing ready-to-buy customers.',
      stat: '89%',
      statText: 'of customers choose one of the first businesses they find.',
      delay: '200ms'
    },
    {
      icon: <TrendingDownIcon />,
      title: 'Lost Revenue Every Day',
      description: 'Poor online visibility isn\'t just a missed opportunity—it\'s a direct and significant impact on your bottom line.',
      stat: '42%',
      statText: 'average revenue loss from poor local search presence.',
      delay: '300ms'
    }
  ];

  return (
    <SectionContainer>
      <ContentWrapper>
        {/* Section Header */}
        <HeaderWrapper ref={headerRef} className="animate-on-scroll">
          <Title>
            Your Local Business is Losing Customers <GradientText>Every Single Day</GradientText>
          </Title>
          <Subtitle>
            Stop the loss, start the growth—easy-to-use tools for lasting business success.
          </Subtitle>
        </HeaderWrapper>

        {/* Problem Cards Grid */}
        <CardsGrid>
          {problems.map((problem, index) => (
            <Card 
              key={index}
              delay={problem.delay}
              ref={(el) => (cardsRef.current[index] = el)}
              className="animate-on-scroll"
            >
              <CardContent>
                <IconContainer>
                  <IconBox>{problem.icon}</IconBox>
                </IconContainer>
                <CardTitle>{problem.title}</CardTitle>
                <CardDescription>{problem.description}</CardDescription>
                <StatContainer>
                  <StatNumber>{problem.stat}</StatNumber>
                  <StatText>{problem.statText}</StatText>
                </StatContainer>
              </CardContent>
            </Card>
          ))}
        </CardsGrid>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default ProblemSection;
